import { Link, type LinkProps } from '@geberit/gdds';
import key from 'weak-key';

// types
import type { ContentProps } from './ContentProps';

// utils
import { popUpLink, internalMediumLink } from './trackingActions';
import { classNameBuilder } from 'utils/classNameBuilder';
import { decodingContent } from 'utils/decodingContent';
import { useTracking } from 'utils/hooks/useTracking';
import { useGdds } from 'utils/hooks/use-gdds';
import { useNord } from 'utils/hooks/use-nord';
import { handlePopup } from 'utils/openInPopup';
import { isEmpty } from 'utils/is-empty';

interface PopUpLinkProps {
  content: ContentProps;
  className?: string;
  onClick?: (e?: React.MouseEvent<HTMLAnchorElement>) => void;
  children?: any;
  tracking?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  standardFontSize?: boolean;
  stylingType?: LinkProps['stylingType'];
  elementType?: string;
  alignByContent?: LinkProps['alignByContent'];
  inContext?: boolean;
}

export function PopUpLink({
  content,
  content: { showArrow, localMedium, target, targeturl, popupArgs },
  className,
  onClick,
  tracking,
  children,
  stylingType,
  elementType,
  standardFontSize = true,
  inContext,
  alignByContent,
}: Readonly<PopUpLinkProps>) {
  const isGdds = useGdds();
  const isNord = useNord();
  const track = useTracking();
  const windowId = key(content);
  const linkText = isEmpty(children) ? decodingContent(content.text) : children;
  const stylingChange = isNord && !showArrow ? 'primary' : 'secondary';
  const isCTA = elementType === 'call_to_action_button';

  // if it has a specific tracking use it else take the default tracking for popups
  const handleTracking = (e) => {
    const link = e.currentTarget.href;
    if (tracking) {
      tracking(e);
    } else if (localMedium) {
      track.trackEvent(internalMediumLink(link));
    } else {
      track.trackEvent(popUpLink(targeturl ?? target ?? link));
    }
  };

  const handlePopupLink = (e) => {
    e.preventDefault();
    if (typeof onClick === 'function') {
      onClick(e);
    }
    handleTracking(e);
    handlePopup(content, windowId);
  };

  // generate classNames
  let classes;
  if (!className?.includes('arrow') || !className?.includes('noArrow')) {
    classes = `${className} ${showArrow ? 'arrow' : 'noArrow'}`;
  }

  /*
   * If isGdds GDDS-Link usage
   */
  if (isGdds && !isCTA) {
    return (
      <Link
        stylingType={stylingType || stylingChange}
        text={linkText}
        hasArrow={showArrow}
        target={content.target}
        standardFontSize={standardFontSize}
        /* eslint-disable-next-line no-script-url */
        onClick={content.target.includes('javascript:') ? handleTracking : handlePopupLink}
        windowTarget=""
        inContext={inContext}
        className={classNameBuilder(className, isNord && 'isNordicsStyle')}
        alignByContent={alignByContent}
      />
    );
  }

  /* eslint-disable-next-line no-script-url */
  if (popupArgs) {
    return (
      <a
        href={content.target}
        className={className}
        onClick={(event) => {
          event.preventDefault();
          handleTracking(event);
          window.open(...popupArgs);
        }}
        aria-label={children ? undefined : (content.text ?? undefined)}
      >
        {linkText}
      </a>
    );
  }

  /*
   * If !isGdds <a> usage
   */
  return (
    <a href={content.target} onClick={handlePopupLink} className={classes}>
      {linkText}
    </a>
  );
}
